import React, { useEffect, useState } from 'react';
import {
	Box,
	CircularProgress,
	IconButton,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import { showToast } from '@/features/toast/toastSlice';
import { useObjectContext } from '@/hooks/useObjectContext';
import {
	useListPortfolioStatementsQuery,
	useGeneratePortfolioStatementMutation,
	useUpdatePortfolioStatementMutation,
} from '@/features/deals/dealsApi';
import { FolderSelectDialog } from '@/components/dialogs/FolderSelectDialog';
import { useMsGraph } from '@/hooks/useMsGraph';
import { useBannerColours } from '@/hooks/useBannerColours';

const getFileNameFromDownloadUrl = (url) => {
	try {
		const withoutQuery = decodeURIComponent(url).split('?')[0];
		const afterBucketPath = withoutQuery.split('/o/')[1] ?? withoutQuery;
		return afterBucketPath.split('/').pop() || 'Portfolio Statement.pdf';
	} catch {
		return 'Portfolio Statement.pdf';
	}
};

const RetryButton = ({ title, disabled, onClick, Icon }) => {
	return (
		<Tooltip title={title}>
			<span>
				<IconButton size='small' aria-label={title} disabled={disabled} onClick={onClick}>
					{disabled ? (
						<CircularProgress size='16px' />
					) : (
						<Icon sx={{ fontSize: '18px' }} />
					)}
				</IconButton>
			</span>
		</Tooltip>
	);
};

const PortfolioStatementBanner = () => {
	const dispatch = useDispatch();
	const { dealId, dealRow } = useObjectContext();
	const { uploadFileAndRetry } = useMsGraph();
	const { backgroundColor, border, textColor } = useBannerColours('error');

	const [folderDialogOpen, setFolderDialogOpen] = useState(false);
	const [isSavingToSharepoint, setIsSavingToSharepoint] = useState(false);

	const { data: portfolioStatements } = useListPortfolioStatementsQuery(
		{ dealId, latest: true },
		{ skip: !dealId },
	);

	const [generatePortfolioStatement, { isLoading: isGenerating }] =
		useGeneratePortfolioStatementMutation();
	const [updatePortfolioStatement, { isLoading: isUpdatingStatus }] =
		useUpdatePortfolioStatementMutation();

	const latest = portfolioStatements?.[0] ?? null;

	// Only the most recent statement matters — an old failure shouldn't show once a later attempt succeeded
	if (!dealId || !latest || latest.status !== 'failed') return null;

	const failureReasons = latest.failureReasons ?? [];
	const generationFailed =
		failureReasons.includes('insightFailed') || failureReasons.includes('firebaseFailed');
	const sharepointOnlyFailed = !generationFailed && failureReasons.includes('sharepointFailed');

	if (!generationFailed && !sharepointOnlyFailed) return null;

	const handleRetryGeneration = async () => {
		try {
			await generatePortfolioStatement({ dealId, policyIds: latest.policyIds }).unwrap();
			await updatePortfolioStatement({
				dealId,
				docId: latest.id,
				status: 'pending',
			}).unwrap();
			dispatch(
				showToast({ message: 'Portfolio statement generation restarted', success: true }),
			);
		} catch (e) {
			console.error('[PortfolioStatementBanner] Failed to retry generation', e);
			dispatch(
				showToast({
					message: 'Failed to restart portfolio statement generation',
					error: true,
				}),
			);
		}
	};

	const handleFolderConfirmed = async ({ selectedFolder, selectedDrive }) => {
		setFolderDialogOpen(false);
		setIsSavingToSharepoint(true);
		try {
			const response = await fetch(latest.downloadUrl);
			const blob = await response.blob();
			const fileName = latest.fileName || getFileNameFromDownloadUrl(latest.downloadUrl);
			const file = new File([blob], fileName, {
				type: blob.type || 'application/pdf',
			});

			const uploaded = await uploadFileAndRetry({
				driveId: selectedDrive?.parentReference?.driveId,
				itemId: selectedFolder,
				file,
				silent: true,
			});

			if (!uploaded) {
				throw new Error('Upload to SharePoint failed');
			}

			await updatePortfolioStatement({
				dealId,
				docId: latest.id,
				status: 'success',
			}).unwrap();
			dispatch(
				showToast({ message: 'Portfolio statement saved to SharePoint', success: true }),
			);
		} catch (e) {
			console.error('[PortfolioStatementBanner] Failed to save to SharePoint', e);
			dispatch(
				showToast({
					message: 'Failed to save portfolio statement to SharePoint',
					error: true,
				}),
			);
		} finally {
			setIsSavingToSharepoint(false);
		}
	};

	const message = generationFailed
		? 'Latest client portfolio statement generation failed'
		: 'Latest client portfolio statement generation succeeded but failed to save';

	const isRetryBusy = isGenerating || isUpdatingStatus;
	const isSaveBusy = isSavingToSharepoint || isUpdatingStatus;

	return (
		<Stack
			direction='row'
			spacing={1}
			sx={{
				alignItems: 'center',
				justifyContent: 'space-between',
				px: 1,
				borderRadius: '4px',
				backgroundColor,
				border,
			}}
		>
			<Typography variant='task_updated' sx={{ color: textColor }}>
				{message}
			</Typography>
			{generationFailed ? (
				<RetryButton
					title='Retry portfolio statement generation'
					disabled={isRetryBusy}
					onClick={handleRetryGeneration}
					Icon={RefreshIcon}
				/>
			) : (
				<>
					<RetryButton
						title='Save portfolio statement to Sharepoint'
						disabled={isSaveBusy}
						onClick={() => setFolderDialogOpen(true)}
						Icon={DriveFolderUploadIcon}
					/>

					<Formik
						initialValues={{
							deal: dealRow,
							selectedFolder: null,
							selectedDrive: null,
							folderPath: '',
						}}
						onSubmit={() => {}}
					>
						<FolderSelectDialog
							initialItemId={dealRow?.driveId}
							open={folderDialogOpen}
							handleClose={() => setFolderDialogOpen(false)}
							onConfirm={handleFolderConfirmed}
						/>
					</Formik>
				</>
			)}
		</Stack>
	);
};

export default PortfolioStatementBanner;
